// Audio math for team voice. No DOM: this file is imported by the audio
// worklets as well as the main thread and the tests.

/**
 * Streaming resampler from the device rate (usually 48 kHz) down to 16 kHz.
 * A windowed-sinc low-pass (cutoff 7.2 kHz, 31 taps) runs at the input rate
 * to stop aliasing, then the filtered signal is read at fractional positions
 * with linear interpolation. State carries across calls, so any chunk size
 * works (the worklet feeds 128-sample render quanta).
 */
export class Downsampler {
  readonly ratio: number;
  private readonly kernel: Float32Array;
  private hist: Float32Array;
  private prev = 0;
  private pos = 0;

  constructor(inRate: number, outRate = 16_000) {
    this.ratio = inRate / outRate;
    if (this.ratio > 1) {
      const taps = 31;
      const fc = (0.45 * outRate) / inRate; // cycles per input sample
      const k = new Float32Array(taps);
      const mid = (taps - 1) / 2;
      let sum = 0;
      for (let i = 0; i < taps; i++) {
        const x = i - mid;
        const sinc = x === 0 ? 2 * fc : Math.sin(2 * Math.PI * fc * x) / (Math.PI * x);
        const w = 0.54 - 0.46 * Math.cos((2 * Math.PI * i) / (taps - 1));
        k[i] = sinc * w;
        sum += k[i]!;
      }
      for (let i = 0; i < taps; i++) k[i] = k[i]! / sum;
      this.kernel = k;
    } else {
      this.kernel = new Float32Array([1]);
    }
    this.hist = new Float32Array(this.kernel.length - 1);
  }

  process(input: Float32Array): Float32Array {
    const taps = this.kernel.length;
    const n = input.length;
    if (n === 0) return new Float32Array(0);
    // 1. Low-pass at the input rate, with history from the last call.
    const buf = new Float32Array(taps - 1 + n);
    buf.set(this.hist, 0);
    buf.set(input, taps - 1);
    const y = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      let acc = 0;
      for (let k = 0; k < taps; k++) acc += this.kernel[k]! * buf[i + taps - 1 - k]!;
      y[i] = acc;
    }
    this.hist = buf.slice(buf.length - (taps - 1));
    // 2. Read at fractional positions. Index 0 is the last sample of the
    //    previous call, so positions 0..n cover [prev, y0..y(n-1)].
    const out: number[] = [];
    const at = (i: number) => (i === 0 ? this.prev : y[i - 1]!);
    while (Math.floor(this.pos) + 1 <= n) {
      const i0 = Math.floor(this.pos);
      const f = this.pos - i0;
      out.push(at(i0) * (1 - f) + at(i0 + 1) * f);
      this.pos += this.ratio;
    }
    this.pos -= n;
    this.prev = y[n - 1]!;
    return Float32Array.from(out);
  }
}

/** Collects float samples and emits fixed-size Int16 frames. */
export class Framer {
  private buf: Int16Array;
  private fill = 0;
  constructor(readonly size = 320) {
    this.buf = new Int16Array(size);
  }
  push(samples: Float32Array, emit: (frame: Int16Array) => void): void {
    for (let i = 0; i < samples.length; i++) {
      const s = Math.max(-1, Math.min(1, samples[i]!));
      this.buf[this.fill++] = s < 0 ? Math.round(s * 32768) : Math.round(s * 32767);
      if (this.fill === this.size) {
        emit(this.buf);
        this.buf = new Int16Array(this.size);
        this.fill = 0;
      }
    }
  }
}

/** RMS level of a frame in dBFS (full scale = 0 dB; silence = -Infinity). */
export function rmsDbfs(frame: Int16Array): number {
  if (frame.length === 0) return -Infinity;
  let sum = 0;
  for (let i = 0; i < frame.length; i++) {
    const v = frame[i]! / 32768;
    sum += v * v;
  }
  const rms = Math.sqrt(sum / frame.length);
  return rms > 0 ? 20 * Math.log10(rms) : -Infinity;
}

/**
 * Voice silence gate (API.md section 9: stop sending while the level stays
 * under about -45 dBFS, but keep sending for 300 ms after it drops, so word
 * endings are not clipped).
 */
export class SilenceGate {
  private quietMs: number;
  constructor(
    private readonly thresholdDb = -45,
    private readonly holdMs = 300,
    private readonly frameMs = 20,
  ) {
    this.quietMs = holdMs + frameMs;
  }
  /** True when this frame should be sent. */
  pass(frame: Int16Array): boolean {
    if (rmsDbfs(frame) >= this.thresholdDb) this.quietMs = 0;
    else this.quietMs += this.frameMs;
    return this.quietMs <= this.holdMs;
  }
  /** True while the last frame was loud (for a "you are speaking" dot). */
  get open(): boolean {
    return this.quietMs === 0;
  }
}

/**
 * Streaming upsampler from 16 kHz to the device rate for playback. `pull`
 * supplies the next n source samples (mixed speech, or silence). Linear
 * interpolation is plenty for speech going up in rate.
 */
export class Upsampler {
  private readonly step: number;
  private q: number[] = [0];
  private pos = 0;
  constructor(inRate = 16_000, outRate = 48_000) {
    this.step = inRate / outRate;
  }
  process(out: Float32Array, pull: (n: number) => Float32Array): void {
    if (out.length === 0) return;
    const lastPos = this.pos + this.step * (out.length - 1);
    const needLen = Math.floor(lastPos) + 2;
    if (this.q.length < needLen) {
      const more = pull(needLen - this.q.length);
      for (let i = 0; i < more.length; i++) this.q.push(more[i]!);
      while (this.q.length < needLen) this.q.push(0);
    }
    for (let j = 0; j < out.length; j++) {
      const i0 = Math.floor(this.pos);
      const f = this.pos - i0;
      out[j] = this.q[i0]! * (1 - f) + this.q[i0 + 1]! * f;
      this.pos += this.step;
    }
    const drop = Math.floor(this.pos);
    this.q.splice(0, drop);
    this.pos -= drop;
  }
}
